import { useState, useRef, useEffect } from "react";
import { Mic, Square, Loader2, Copy, Wand2, Sparkles, Trash2, ChevronDown, ChevronUp, Download, Zap } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { OUTPUT_MODES, LANGUAGES, TRANSFORMS } from "@/lib/brand";
import { downloadText, tsFilename, fmtMs } from "@/lib/download";
import WaveformBars from "@/components/Waveform.jsx";
import { useAuth } from "@/lib/auth.jsx";

const STAGES = {
  idle: "Ready",
  countdown: "Get ready…",
  recording: "Recording…",
  uploading: "Uploading…",
  transcribing: "Transcribing…",
  polishing: "Polishing…",
  done: "Ready",
};

export default function Dashboard() {
  const { refresh } = useAuth();
  const [language, setLanguage] = useState("en");
  const [outputMode, setOutputMode] = useState("smart_english");
  const [stage, setStage] = useState("idle");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [rawText, setRawText] = useState("");
  const [polished, setPolished] = useState("");
  const [recordSeconds, setRecordSeconds] = useState(0);
  const [countdown, setCountdown] = useState(0);
  const [detectedLang, setDetectedLang] = useState("");
  const [hasLastBlob, setHasLastBlob] = useState(false);
  const [timings, setTimings] = useState({ stt_ms: null, polish_ms: null, total_ms: null });
  const [sttProvider, setSttProvider] = useState("");  // "google" / "openai" / "mock"
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const lastBlobRef = useRef(null);
  const flowStartedAt = useRef(0);

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);
  // Refresh user word usage whenever a pipeline completes.
  useEffect(() => { if (stage === "done") refresh(); }, [stage, refresh]);

  const recording = stage === "recording";
  const busy = ["uploading", "transcribing", "polishing", "countdown"].includes(stage);

  const startRecording = async () => {
    // Brief countdown so the user's first word is not clipped.
    setStage("countdown");
    for (let i = 2; i >= 1; i--) {
      setCountdown(i);
      await new Promise((r) => setTimeout(r, 400));
    }
    setCountdown(0);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 48000,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      // Pick the best supported mime type for Whisper / Google STT
      const mime = ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus", "audio/mp4"]
        .find((m) => window.MediaRecorder && MediaRecorder.isTypeSupported(m)) || "audio/webm";
      const mr = new MediaRecorder(stream, { mimeType: mime, audioBitsPerSecond: 128000 });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        // Give the recorder a final 250ms so the trailing word isn't lost
        await new Promise((r) => setTimeout(r, 250));
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mime });
        lastBlobRef.current = blob;
        setHasLastBlob(true);
        await transcribe(blob);
      };
      mr.start(250); // collect chunks every 250ms — smoother waveform & flush
      mediaRecorderRef.current = mr;
      setStage("recording");
      setRecordSeconds(0);
      timerRef.current = setInterval(() => setRecordSeconds((s) => s + 1), 1000);
    } catch (e) {
      setStage("idle");
      toast.error("Microphone permission denied. Please allow mic access and try again.");
    }
  };

  const stopRecording = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setStage("uploading");
    flowStartedAt.current = Date.now();
    setTimings({ stt_ms: null, polish_ms: null, total_ms: null });
    try { mediaRecorderRef.current?.stop(); } catch { /* swallow */ }
  };

  const transcribe = async (blob, attempt = 1) => {
    setStage("transcribing");
    try {
      const form = new FormData();
      form.append("audio", blob, "voice.webm");
      form.append("language", language);
      const { data } = await api.post("/voice/transcribe", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      const text = (data.text || "").trim();
      setDetectedLang(data.detected_language || language);
      setSttProvider(data.provider || "mock");
      setTimings((t) => ({ ...t, stt_ms: data.stt_ms ?? null }));
      if (!text) {
        toast.error("No speech detected — please speak a little louder and retry.");
        setStage("idle");
        return;
      }
      setRawText(text);
      // If user is in raw mode, we are done — preserve language, no LLM.
      if (outputMode === "raw") {
        setPolished(text);
        try { await api.post("/ai/polish", { raw_text: text, output_mode: "raw", language, save_history: true }); } catch { /* swallow */ }
        setTimings((t) => ({ ...t, total_ms: Date.now() - (flowStartedAt.current || Date.now()) }));
        setStage("done");
      } else {
        await polish(text);
      }
    } catch (e) {
      if (attempt < 2) {
        toast.message("Retrying transcription…");
        return transcribe(blob, attempt + 1);
      }
      toast.error(e?.response?.data?.detail || "Transcription failed. Please try again.");
      setStage("idle");
    }
  };

  const polish = async (text) => {
    const source = (text ?? rawText).trim();
    if (!source) { toast.error("Nothing to polish — record or type something first."); setStage("idle"); return; }
    setStage("polishing");
    if (!flowStartedAt.current) flowStartedAt.current = Date.now();
    try {
      const { data } = await api.post("/ai/polish", {
        raw_text: source, output_mode: outputMode, language, save_history: true,
      });
      setPolished(data.polished_text);
      const total = Date.now() - flowStartedAt.current;
      setTimings((t) => ({ ...t, polish_ms: data.polish_ms ?? null, total_ms: total }));
      setStage("done");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Polish failed");
      setStage("idle");
    }
  };

  const transform = async (id) => {
    if (!polished.trim()) { toast.error("Nothing to transform — record or type first."); return; }
    setStage("polishing");
    try {
      const { data } = await api.post("/ai/transform", { text: polished, transform: id });
      setPolished(data.text);
      toast.success("Transformed.");
      setStage("done");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Transform failed");
      setStage("idle");
    }
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied!"); } catch { toast.error("Copy failed"); }
  };

  const fmtTime = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  const stageLabel = STAGES[stage] || "Ready";

  return (
    <div className="px-5 lg:px-10 py-8 max-w-7xl mx-auto">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-brand-navy">Voice Studio</h1>
          <p className="text-sm text-brand-muted mt-1">
            Tap the mic and speak naturally. Default: <strong>English</strong> · <strong>Smart English</strong>.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {detectedLang && (
            <span className="text-xs px-3 py-1 rounded-full bg-brand-surface text-brand-navy">
              Detected: <strong>{detectedLang}</strong>
            </span>
          )}
          {sttProvider && sttProvider !== "mock" && (
            <span className="text-xs px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
              STT: <strong>{sttProvider}</strong>
            </span>
          )}
          {sttProvider === "mock" && (
            <span data-testid="mock-chip" className="text-xs px-3 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200">
              Google STT active
            </span>
          )}
          {(timings.stt_ms || timings.polish_ms || timings.total_ms) && (
            <span data-testid="timings-chip" className="text-xs px-3 py-1 rounded-full bg-white border border-brand-borderc text-brand-text inline-flex items-center gap-1.5">
              <Zap className="w-3 h-3 text-brand-purple" />
              STT {fmtMs(timings.stt_ms)} · Polish {fmtMs(timings.polish_ms)} · Total {fmtMs(timings.total_ms)}
            </span>
          )}
        </div>
      </div>

      {/* Advanced (language + output mode) — both hidden by default */}
      <div className="bg-white rounded-2xl border border-brand-borderc">
        <button
          data-testid="toggle-advanced"
          onClick={() => setShowAdvanced((v) => !v)}
          className="w-full flex items-center justify-between px-5 py-4 text-left"
        >
          <div>
            <div className="text-sm font-semibold text-brand-navy">Advanced — language & output mode</div>
            <div className="text-xs text-brand-muted mt-0.5">
              Currently <strong className="text-brand-text">{LANGUAGES.find((l) => l.id === language)?.label}</strong>
              {" · "}
              <strong className="text-brand-text">{OUTPUT_MODES.find((m) => m.id === outputMode)?.label}</strong>
              {" · click to change"}
            </div>
          </div>
          {showAdvanced ? <ChevronUp className="w-5 h-5 text-brand-muted" /> : <ChevronDown className="w-5 h-5 text-brand-muted" />}
        </button>
        {showAdvanced && (
          <div className="px-5 pb-5 space-y-4">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-brand-muted">Language</div>
              <div className="mt-2 grid grid-cols-2 md:grid-cols-5 gap-2">
                {LANGUAGES.map((l) => (
                  <button
                    key={l.id} data-testid={`lang-${l.id}`}
                    onClick={() => setLanguage(l.id)}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-medium transition ${
                      language === l.id ? "bg-gradient-primary text-white border-transparent" : "bg-white border-brand-borderc text-brand-text hover:bg-brand-surface"
                    }`}
                  >
                    <span>{l.flag}</span>{l.label}
                  </button>
                ))}
              </div>
              <p className="mt-2 text-[11px] text-brand-muted">
                <strong>Select the language you are speaking.</strong> English is the default. Choose Hindi or Telugu before recording.
              </p>
            </div>

            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-brand-muted">Output mode</div>
              <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-2">
                {OUTPUT_MODES.map((m) => (
                  <button
                    key={m.id} data-testid={`mode-${m.id}`}
                    onClick={() => setOutputMode(m.id)}
                    className={`text-left p-3 rounded-xl border transition ${
                      outputMode === m.id ? "border-brand-purple bg-[#F4F2FF]" : "border-brand-borderc bg-white hover:bg-brand-surface"
                    }`}
                  >
                    <div className="text-sm font-semibold text-brand-navy">{m.label}</div>
                    <div className="text-[11px] text-brand-muted mt-0.5">{m.desc}</div>
                  </button>
                ))}
              </div>
              <p className="mt-2 text-[11px] text-brand-muted">
                <strong>Raw Text</strong> returns exactly what you said in the same language. Other modes use AI to polish.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Recorder */}
      <div className="mt-6 bg-white rounded-3xl border border-brand-borderc p-8 shadow-soft relative overflow-hidden">
        <div className="absolute inset-0 bg-mesh opacity-50 pointer-events-none" />
        <div className="relative flex flex-col items-center">
          <div className="text-xs uppercase tracking-widest font-semibold text-brand-muted">{stageLabel}</div>
          <div className="text-2xl font-mono font-bold text-brand-navy mt-1">
            {stage === "countdown" ? countdown : fmtTime(recordSeconds)}
          </div>

          <div className="relative my-6">
            {recording && <span className="absolute inset-0 rounded-full bg-brand-purple/30 animate-pulse-ring" />}
            <button
              data-testid="mic-toggle-btn"
              onClick={recording ? stopRecording : startRecording}
              disabled={busy}
              className={`relative w-24 h-24 rounded-full flex items-center justify-center shadow-glow-purple transition ${
                recording ? "bg-gradient-warm" : "btn-primary-gradient"
              } disabled:opacity-60`}
            >
              {busy && stage !== "countdown" ? <Loader2 className="w-10 h-10 text-white animate-spin" /> : recording ? <Square className="w-9 h-9 text-white" /> : <Mic className="w-10 h-10 text-white" />}
            </button>
          </div>
          <WaveformBars active={recording || stage === "transcribing"} count={28} />

          {/* Stage progress chips */}
          <div className="mt-4 flex flex-wrap items-center justify-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest">
            {["recording", "uploading", "transcribing", "polishing", "done"].map((s, idx) => {
              const order = ["recording", "uploading", "transcribing", "polishing", "done"];
              const cur = order.indexOf(stage);
              const isActive = stage === s;
              const isDone = cur > idx;
              return (
                <span
                  key={s}
                  className={`px-2.5 py-1 rounded-full ${
                    isActive ? "bg-gradient-primary text-white" : isDone ? "bg-brand-surface text-brand-navy" : "bg-white border border-brand-borderc text-brand-muted"
                  }`}
                >
                  {s === "done" ? "Ready" : s}
                </span>
              );
            })}
          </div>

          <p className="mt-3 text-sm text-brand-muted">
            {recording ? "Tap to stop" : stage === "countdown" ? "Get ready…" : "Tap the mic to start"}
          </p>
        </div>
      </div>

      {/* Transcript + Polished */}
      <div className="grid lg:grid-cols-2 gap-5 mt-6">
        <div className="bg-white rounded-2xl border border-brand-borderc p-5">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-brand-navy">Raw transcript</h3>
            <div className="flex gap-2">
              <button onClick={() => copy(rawText)} disabled={!rawText} className="text-xs px-3 py-1.5 rounded-full bg-brand-surface text-brand-text hover:bg-brand-borderc disabled:opacity-40 inline-flex items-center gap-1">
                <Copy className="w-3.5 h-3.5" /> Copy
              </button>
              <button data-testid="download-raw" onClick={() => downloadText(tsFilename("draftvoice-transcript"), rawText)} disabled={!rawText} className="text-xs px-3 py-1.5 rounded-full bg-brand-surface text-brand-text hover:bg-brand-borderc disabled:opacity-40 inline-flex items-center gap-1">
                <Download className="w-3.5 h-3.5" /> .txt
              </button>
              <button onClick={() => { setRawText(""); setPolished(""); setDetectedLang(""); setTimings({ stt_ms: null, polish_ms: null, total_ms: null }); setSttProvider(""); setStage("idle"); }} className="text-xs px-3 py-1.5 rounded-full bg-brand-surface text-brand-text hover:bg-brand-borderc inline-flex items-center gap-1">
                <Trash2 className="w-3.5 h-3.5" /> Clear
              </button>
            </div>
          </div>
          <textarea
            data-testid="raw-text-area"
            value={rawText} onChange={(e) => setRawText(e.target.value)} rows={9}
            placeholder="Your voice transcript appears here in the language you spoke. You can also type directly."
            className="mt-3 w-full p-3 rounded-xl border border-brand-borderc bg-brand-bg text-sm text-brand-text focus:border-brand-purple focus:ring-2 focus:ring-brand-purple/20 outline-none resize-none"
          />
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              data-testid="polish-btn"
              onClick={() => polish()} disabled={busy || !rawText.trim() || outputMode === "raw"}
              title={outputMode === "raw" ? "Switch from Raw Text in Advanced to enable AI polish" : ""}
              className="btn-primary-gradient rounded-full px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50"
            >
              {stage === "polishing" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Polish with AI
            </button>
            {hasLastBlob && stage === "idle" && (
              <button onClick={() => transcribe(lastBlobRef.current)} className="text-xs px-3 py-2 rounded-full bg-brand-surface hover:bg-brand-borderc">
                Retry transcription
              </button>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-brand-borderc p-5">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-brand-navy">
              {outputMode === "raw" ? "Final output (Raw Text)" : "AI polished output"}
            </h3>
            <div className="flex gap-2">
              <button data-testid="copy-polished-btn" onClick={() => copy(polished)} disabled={!polished} className="text-xs px-3 py-1.5 rounded-full bg-brand-surface text-brand-text hover:bg-brand-borderc disabled:opacity-40 inline-flex items-center gap-1">
                <Copy className="w-3.5 h-3.5" /> Copy
              </button>
              <button data-testid="download-polished" onClick={() => downloadText(tsFilename("draftvoice-draft"), polished)} disabled={!polished} className="text-xs px-3 py-1.5 rounded-full bg-brand-surface text-brand-text hover:bg-brand-borderc disabled:opacity-40 inline-flex items-center gap-1">
                <Download className="w-3.5 h-3.5" /> .txt
              </button>
            </div>
          </div>
          <textarea
            data-testid="polished-text-area"
            value={polished} onChange={(e) => setPolished(e.target.value)} rows={9}
            placeholder={outputMode === "raw" ? "Final output appears here exactly as spoken." : "Polished draft appears here. Edit freely, transform, or copy."}
            className="mt-3 w-full p-3 rounded-xl border border-brand-borderc bg-white text-sm text-brand-text focus:border-brand-purple focus:ring-2 focus:ring-brand-purple/20 outline-none resize-none"
          />
          {outputMode !== "raw" && (
            <div className="mt-3 flex flex-wrap gap-2">
              {TRANSFORMS.map((t) => (
                <button
                  key={t.id} data-testid={`transform-${t.id}`}
                  onClick={() => transform(t.id)} disabled={busy || !polished.trim()}
                  className="text-xs px-3 py-1.5 rounded-full bg-brand-surface text-brand-text hover:bg-brand-purple hover:text-white transition disabled:opacity-40 inline-flex items-center gap-1"
                >
                  <Wand2 className="w-3.5 h-3.5" /> {t.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
